import React, { useState, useEffect } from 'react'
import { X, UserPlus, AlertCircle } from 'lucide-react'
import axiosInstance from '../../../services/axiosInstance'
import { COUNTRIES } from '../../../utils/countries'
import ConfirmationModal from '../ConfirmationModal'
import './styles.scss'

export default function CreateStudentModal({ isOpen, onClose, onSave }) {
  const [loadingData, setLoadingData] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [showConfirmation, setShowConfirmation] = useState(false)

  const [universities, setUniversities] = useState([])
  const [programs, setPrograms] = useState([])
  const [intakes, setIntakes] = useState([])

  const [formData, setFormData] = useState({
    studentFirstName: '',
    studentLastName: '',
    studentEmail: '',
    studentCountryOfCitizenship: '',
    studentCountryOfResidence: '',
    universityId: '',
    programId: '',
    intakeId: ''
  })
  const [confirmStudentEmail, setConfirmStudentEmail] = useState('')

  useEffect(() => {
    if (isOpen) {
      fetchUniversities()
      setFormData({
        studentFirstName: '',
        studentLastName: '',
        studentEmail: '',
        studentCountryOfCitizenship: '',
        universityId: '',
        programId: '',
        intakeId: ''
      })
      setConfirmStudentEmail('')
      setError('')
    }
  }, [isOpen])

  const fetchUniversities = async () => {
    try {
      setLoadingData(true)
      const res = await axiosInstance.get('/api/settings/university/all')
      // The backend returns a plain array, or an object with data array
      const data = res.data.data ? res.data.data : res.data
      setUniversities(Array.isArray(data) ? data : [])
    } catch (err) {
      setError('Failed to load universities.')
    } finally {
      setLoadingData(false)
    }
  }

  const fetchProgramsAndIntakes = async (universityId) => {
    try {
      setLoadingData(true)
      // Since the user is an agent, they shouldn't just fetch their own program, but the selected university's.
      // Wait, /api/settings/programs currently relies on the X-Organization-Id.
      // We can pass the universityId in the header for these requests just for this fetch.
      const [progRes, intRes] = await Promise.all([
        axiosInstance.get('/api/settings/programs', { headers: { 'X-Organization-Id': universityId } }),
        axiosInstance.get('/api/settings/intakes', { headers: { 'X-Organization-Id': universityId } })
      ])
      setPrograms(progRes.data.filter(p => p.active))
      setIntakes(intRes.data.filter(i => i.active))
    } catch (err) {
      setError('Failed to load programs for this university.')
    } finally {
      setLoadingData(false)
    }
  }

  const handleUniversityChange = (e) => {
    const uId = e.target.value
    setFormData({ ...formData, universityId: uId, programId: '', intakeId: '' })
    if (uId) {
      fetchProgramsAndIntakes(uId)
    } else {
      setPrograms([])
      setIntakes([])
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Basic validation
    if (!formData.universityId || !formData.programId || !formData.intakeId) {
      setError('Please select a university, program, and intake.')
      return
    }

    if (formData.studentEmail !== confirmStudentEmail) {
      setError('Email addresses do not match.')
      return
    }

    // Show the confirmation modal instead of saving immediately
    setShowConfirmation(true)
  }

  const handleConfirmCreate = async () => {
    try {
      setSubmitting(true)
      setShowConfirmation(false)
      await onSave(formData)
      onClose()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create student application.')
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay">
      <div className="modal-content large fade-in">
        <div className="modal-header">
          <div className="header-left">
            <div className="modal-icon">
              <UserPlus size={20} />
            </div>
            <div>
              <h2 className="modal-title">New Student Application</h2>
              <p className="modal-subtitle">Create an application on behalf of a student</p>
            </div>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && (
            <div className="form-error">
              {error}
            </div>
          )}

          <h4 className="section-title">Student Information</h4>
          <div className="form-row">
            <div className="form-group">
              <label>First Name (Legal) *</label>
              <input
                type="text"
                required
                placeholder="e.g. Jane"
                value={formData.studentFirstName}
                onChange={(e) => setFormData({ ...formData, studentFirstName: e.target.value })}
                disabled={submitting}
              />
            </div>
            <div className="form-group">
              <label>Last Name (Legal) *</label>
              <input
                type="text"
                required
                placeholder="e.g. Doe"
                value={formData.studentLastName}
                onChange={(e) => setFormData({ ...formData, studentLastName: e.target.value })}
                disabled={submitting}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email Address *</label>
              <input
                type="email"
                required
                placeholder="jane.doe@example.com"
                value={formData.studentEmail}
                onChange={(e) => setFormData({ ...formData, studentEmail: e.target.value })}
                disabled={submitting}
              />
            </div>
            <div className="form-group">
              <label>Confirm Email Address *</label>
              <input
                type="email"
                required
                placeholder="jane.doe@example.com"
                value={confirmStudentEmail}
                onChange={(e) => setConfirmStudentEmail(e.target.value)}
                disabled={submitting}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Country of Citizenship *</label>
              <select 
                name="studentCountryOfCitizenship"
                value={formData.studentCountryOfCitizenship} 
                onChange={(e) => setFormData({ ...formData, studentCountryOfCitizenship: e.target.value })} 
                required
                disabled={submitting}
              >
                <option value="">Select Country</option>
                {COUNTRIES.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.flag} {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Country of Residence *</label>
              <select 
                name="studentCountryOfResidence"
                value={formData.studentCountryOfResidence} 
                onChange={(e) => setFormData({ ...formData, studentCountryOfResidence: e.target.value })} 
                required
                disabled={submitting}
              >
                <option value="">Select Country</option>
                {COUNTRIES.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.flag} {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <h4 className="section-title">Application Details</h4>
          <div className="form-group">
            <label>University *</label>
            <select
              value={formData.universityId}
              onChange={handleUniversityChange}
              required
              disabled={submitting || loadingData}
            >
              <option value="" disabled>Select University...</option>
              {universities.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Program *</label>
              <select
                value={formData.programId}
                onChange={(e) => setFormData({ ...formData, programId: e.target.value })}
                required
                disabled={submitting || loadingData || !formData.universityId}
              >
                <option value="" disabled>Select Program...</option>
                {programs.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Intake *</label>
              <select
                value={formData.intakeId}
                onChange={(e) => setFormData({ ...formData, intakeId: e.target.value })}
                required
                disabled={submitting || loadingData || !formData.universityId}
              >
                <option value="" disabled>Select Intake...</option>
                {intakes.map(i => (
                  <option key={i.id} value={i.id}>{i.term} {i.year}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-warning">
            <AlertCircle size={18} className="warning-icon" />
            <p>
              <strong>Important:</strong> Please double-check the student's email address. Login credentials and sensitive application updates will be sent to this exact address. If an incorrect email is entered, the application cannot be recovered by the student.
            </p>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={submitting || loadingData}>
              {submitting ? 'Creating...' : 'Create Application'}
            </button>
          </div>
        </form>
      </div>

      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirmCreate}
        title="Create Student Application"
        message={
          <>
            Are you sure you want to create an application for <strong>{formData.studentFirstName} {formData.studentLastName}</strong>? 
            <br/><br/>
            An email will be sent immediately to <strong>{formData.studentEmail}</strong> with their login credentials and next steps.
          </>
        }
        confirmText="Yes, Create Application"
        cancelText="Cancel"
      />
    </div>
  )
}
